import { supabase } from './supabaseClient';
import { trackWizardComplete, trackContactSubmit } from '../utils/tiktokPixel';
import { trackServerEvent } from './tiktokEventsApi';
import { submitWizardLead, updateFrappeLead } from './frappeLeadService';

/**
 * Configuration for Frappe submission reliability
 */
const FRAPPE_CONFIG = {
  // Maximum retries for Frappe submission
  maxRetries: 5,
  // Whether Frappe failure should block submission success
  // Set to true to ensure leads are ALWAYS created (rock-solid mode)
  blockOnFrappeFailure: true,
  // Background retry configuration for failed Frappe submissions
  enableBackgroundRetry: true,
  backgroundRetryDelayMs: 30000, // 30 seconds
};

/**
 * Queue for failed Frappe submissions (in-memory for current session)
 * In production, this should be persisted to localStorage or IndexedDB
 */
const failedFrappeSubmissions = [];

/**
 * Wizard Submission Service for saving completed wizard forms to Supabase
 * with rock-solid Frappe CRM lead creation
 */
export class WizardSubmissionService {

  /**
   * Save a completed wizard submission to Supabase AND Frappe CRM
   * Implements a confirmation loop to ensure leads are created
   * @param {Object} submissionData - Complete wizard submission data
   * @returns {Object} - Saved submission record with Frappe status
   */
  static async saveSubmission(submissionData) {
    let supabaseData = null;
    let frappeResult = null;
    
    try {
      // Prepare the submission data for Supabase
      const submission = {
        // User Information
        first_name: submissionData.personalInfo?.firstName || '',
        last_name: submissionData.personalInfo?.lastName || '',
        email: submissionData.personalInfo?.email || '',
        phone: submissionData.personalInfo?.phone || '',

        // Wizard Data (JSON)
        country_selection: submissionData.countrySelection || {},
        personal_info: submissionData.personalInfo || {},
        education_info: submissionData.education || {},
        experience_info: submissionData.experience || [],
        language_info: submissionData.languageSkills || {},
        financial_info: submissionData.financialInfo || {},

        // CV Data (if uploaded)
        cv_data: submissionData.cvData || null,

        // Pathway and Package Selection
        selected_pathway: submissionData.selectedPathway || null,
        selected_package: submissionData.selectedPackage || null,
        pathway_score: submissionData.scores || submissionData.scoringResults || {},

        // Contact Preferences
        contact_preference: submissionData.contactPreference || 'email',
        
        // Frappe sync status (new field)
        frappe_sync_status: 'pending',
        frappe_lead_id: null,
      };

      console.log('[WizardSubmission] Step 1: Saving to Supabase...', { email: submission.email });

      // Step 1: Save to Supabase first
      const { data, error } = await supabase
        .from('wizard_submissions')
        .insert([submission])
        .select()
        .single();

      if (error) {
        console.error('[WizardSubmission] Supabase save failed:', error);
        throw error;
      }

      supabaseData = data;
      console.log('[WizardSubmission] Step 1 Complete: Supabase save successful', { id: data.id });

      // Step 2: Handle Frappe CRM sync - check for existing lead from CV upload
      console.log('[WizardSubmission] Step 2: Checking Frappe CRM status...');

      const existingFrappeLeadId = submissionData.frappeLeadId;
      let finalFrappeLeadId = existingFrappeLeadId;
      let frappeOperation = existingFrappeLeadId ? 'update' : 'create';

      try {
        if (existingFrappeLeadId) {
          // UPDATE existing lead created at CV upload
          console.log('[WizardSubmission] Updating existing Frappe lead:', existingFrappeLeadId);

          frappeResult = await updateFrappeLead(existingFrappeLeadId, submissionData, {
            maxRetries: FRAPPE_CONFIG.maxRetries,
            throwOnFail: FRAPPE_CONFIG.blockOnFrappeFailure,
          });

          if (frappeResult.success) {
            console.log('[WizardSubmission] Step 2 Complete: Frappe lead updated', {
              leadId: frappeResult.leadId,
              attempts: frappeResult.attempts,
            });
          } else {
            console.warn('[WizardSubmission] Frappe update failed, will retry:', frappeResult.error);
            // Keep the original lead ID for retry purposes
          }
        } else {
          // CREATE new lead (no lead was created at CV upload)
          console.log('[WizardSubmission] Creating new Frappe lead...');

          frappeResult = await submitWizardLead(submissionData, {
            maxRetries: FRAPPE_CONFIG.maxRetries,
            throwOnFail: FRAPPE_CONFIG.blockOnFrappeFailure,
          });

          if (frappeResult.success) {
            console.log('[WizardSubmission] Step 2 Complete: Frappe lead created', {
              leadId: frappeResult.leadId,
              attempts: frappeResult.attempts,
            });
            finalFrappeLeadId = frappeResult.leadId;
          } else {
            console.warn('[WizardSubmission] Frappe creation failed (non-blocking):', frappeResult.error);
          }
        }

        // Update Supabase with final Frappe status
        if (frappeResult?.success) {
          await this.updateFrappeStatus(data.id, 'synced', finalFrappeLeadId);
        } else {
          await this.updateFrappeStatus(data.id, 'failed', finalFrappeLeadId, frappeResult?.error);

          // Queue for background retry
          if (FRAPPE_CONFIG.enableBackgroundRetry) {
            this.queueForRetry(data.id, submissionData);
          }
        }
      } catch (frappeError) {
        // Handle both update and create failures
        console.error('[WizardSubmission] Frappe operation FAILED:', frappeError.message);

        await this.updateFrappeStatus(data.id, 'failed', finalFrappeLeadId, frappeError.message);

        // Queue for background retry
        if (FRAPPE_CONFIG.enableBackgroundRetry) {
          this.queueForRetry(data.id, submissionData);
        }

        // If blocking mode, throw to indicate failure
        if (FRAPPE_CONFIG.blockOnFrappeFailure) {
          const combinedError = new Error(
            `Lead ${frappeOperation === 'update' ? 'update' : 'creation'} failed after ${FRAPPE_CONFIG.maxRetries + 1} attempts. ` +
            `Our team will process it manually. Error: ${frappeError.message}`
          );
          combinedError.supabaseData = data;
          combinedError.frappeError = frappeError;
          combinedError.partialSuccess = true; // Indicates Supabase succeeded
          throw combinedError;
        }
      }

      // Step 3: Tracking pixels
      const eventId = crypto.randomUUID ? crypto.randomUUID() : `evt_${Date.now()}_${Math.random().toString(36).slice(2)}`;

      trackWizardComplete({ submissionId: data?.id }, eventId);
      trackContactSubmit({
        email: submission.email,
        phone: submission.phone
      }, eventId);

      trackServerEvent('CompleteRegistration', {
        content_type: 'product',
        content_id: 'wizard_complete',
        value: 1,
        currency: 'USD',
      }, eventId, {
        email: submission.email,
        phone: submission.phone
      });

      console.log('[WizardSubmission] All steps complete!', {
        supabaseId: data.id,
        frappeStatus: frappeResult?.success ? 'synced' : 'pending',
        frappeLeadId: frappeResult?.leadId,
      });

      return {
        ...data,
        frappe_sync_status: frappeResult?.success ? 'synced' : 'failed',
        frappe_lead_id: frappeResult?.leadId || null,
        frappe_attempts: frappeResult?.attempts,
      };

    } catch (error) {
      console.error('[WizardSubmission] Error in saveSubmission:', error);
      
      // If it's a partial success (Supabase worked, Frappe failed), return partial data
      if (error.partialSuccess && error.supabaseData) {
        return {
          ...error.supabaseData,
          frappe_sync_status: 'failed',
          frappe_error: error.frappeError?.message,
          _warning: 'Lead saved to database but CRM sync failed. Will retry automatically.',
        };
      }
      
      throw error;
    }
  }

  /**
   * Update Frappe sync status in Supabase
   */
  static async updateFrappeStatus(submissionId, status, leadId = null, errorMessage = null) {
    try {
      const updateData = {
        frappe_sync_status: status,
        frappe_lead_id: leadId,
        frappe_sync_error: errorMessage,
        frappe_synced_at: status === 'synced' ? new Date().toISOString() : null,
      };

      const { error } = await supabase
        .from('wizard_submissions')
        .update(updateData)
        .eq('id', submissionId);

      if (error) {
        console.warn('[WizardSubmission] Failed to update Frappe status:', error);
      }
    } catch (e) {
      console.warn('[WizardSubmission] Error updating Frappe status:', e);
    }
  }

  /**
   * Queue failed Frappe submission for background retry
   */
  static queueForRetry(submissionId, submissionData) {
    console.log('[WizardSubmission] Queuing for background retry:', submissionId);
    
    failedFrappeSubmissions.push({
      submissionId,
      submissionData,
      queuedAt: Date.now(),
      retryCount: 0,
    });

    // Start background retry worker if not already running
    this.startBackgroundRetryWorker();
  }

  /**
   * Background retry worker for failed Frappe submissions
   */
  static backgroundRetryWorkerRunning = false;
  
  static startBackgroundRetryWorker() {
    if (this.backgroundRetryWorkerRunning) return;
    this.backgroundRetryWorkerRunning = true;

    const processQueue = async () => {
      while (failedFrappeSubmissions.length > 0) {
        const item = failedFrappeSubmissions[0];
        
        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, FRAPPE_CONFIG.backgroundRetryDelayMs));
        
        console.log('[WizardSubmission] Background retry attempt for:', item.submissionId);
        
        try {
          const result = await submitWizardLead(item.submissionData, {
            maxRetries: 2,
            throwOnFail: false,
          });

          if (result.success) {
            console.log('[WizardSubmission] Background retry SUCCESS:', item.submissionId);
            await this.updateFrappeStatus(item.submissionId, 'synced', result.leadId);
            failedFrappeSubmissions.shift(); // Remove from queue
          } else {
            item.retryCount++;
            if (item.retryCount >= 3) {
              console.error('[WizardSubmission] Background retry exhausted for:', item.submissionId);
              failedFrappeSubmissions.shift();
            }
          }
        } catch (e) {
          console.error('[WizardSubmission] Background retry error:', e);
          item.retryCount++;
          if (item.retryCount >= 3) {
            failedFrappeSubmissions.shift();
          }
        }
      }
      
      this.backgroundRetryWorkerRunning = false;
    };

    processQueue();
  }

  /**
   * Manually retry failed Frappe submissions for a specific submission
   */
  static async retryFrappeSync(submissionId) {
    try {
      // Get submission from Supabase
      const { data, error } = await supabase
        .from('wizard_submissions')
        .select('*')
        .eq('id', submissionId)
        .single();

      if (error || !data) {
        throw new Error('Submission not found');
      }

      // Reconstruct wizard data from submission
      const wizardData = {
        personalInfo: data.personal_info,
        countrySelection: data.country_selection,
        education: data.education_info,
        experience: data.experience_info,
        languageSkills: data.language_info,
        financialInfo: data.financial_info,
        cvData: data.cv_data,
        selectedPathway: data.selected_pathway,
        selectedPackage: data.selected_package,
        scores: data.pathway_score,
        contactPreference: data.contact_preference,
      };

      console.log('[WizardSubmission] Manual retry for submission:', submissionId);
      
      const result = await submitWizardLead(wizardData, {
        maxRetries: 5,
        throwOnFail: true,
      });

      await this.updateFrappeStatus(submissionId, 'synced', result.leadId);

      return result;
    } catch (error) {
      console.error('[WizardSubmission] Manual retry failed:', error);
      throw error;
    }
  }

  /**
   * Get all submissions with failed Frappe sync
   */
  static async getFailedFrappeSyncs() {
    try {
      const { data, error } = await supabase
        .from('wizard_submissions')
        .select('id, email, first_name, last_name, created_at, frappe_sync_status, frappe_sync_error')
        .eq('frappe_sync_status', 'failed')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('[WizardSubmission] Error fetching failed syncs:', error);
      throw error;
    }
  }

  /**
   * Get a specific submission by ID
   * @param {string} submissionId - The submission ID
   * @returns {Object} - Submission data
   */
  static async getSubmission(submissionId) {
    try {
      const { data, error } = await supabase
        .from('wizard_submissions')
        .select('*')
        .eq('submission_id', submissionId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching submission:', error);
      throw error;
    }
  }

  /**
   * Syncs the uploaded CV to the submission_documents table
   * so it appears in the regular Documents Manager.
   */
  static async syncCVToDocuments(submissionId, cvData) {
    if (!submissionId || !cvData) return;

    try {
      const title = `CV - ${cvData.fileName || 'Uploaded CV'}`;
      const filePath = cvData.storagePath || cvData.path;
      const fileUrl = cvData.url || cvData.publicUrl;

      if (!filePath || !fileUrl) {
        console.warn('Skipping CV sync: missing path or url', cvData);
        return;
      }

      // Check if already exists to avoid duplicates
      const { data: existing } = await supabase
        .from('submission_documents')
        .select('id')
        .eq('submission_id', submissionId)
        .eq('file_path', filePath)
        .single();

      if (existing) return;

      const { error } = await supabase
        .from('submission_documents')
        .insert({
          submission_id: submissionId,
          title: title,
          file_name: cvData.fileName || 'cv.pdf',
          file_path: filePath,
          file_url: fileUrl,
          file_size: cvData.fileSize || 0,
          file_type: cvData.fileType || 'application/pdf',
          status: 'uploaded',
          is_internal: false,
          created_at: new Date().toISOString()
        });

      if (error) throw error;
      console.log('CV synced to submission_documents successfully');
    } catch (error) {
      console.error('Error syncing CV to documents:', error);
      // We don't throw here to avoid blocking the main flow if this side-effect fails
    }
  }

  /**
   * Batch syncs all existing CVs to submission_documents
   * Useful for backfilling historical data.
   */
  static async syncAllCVs() {
    try {
      console.log('Starting batch CV sync...');
      const { data: submissions, error } = await supabase
        .from('wizard_submissions')
        .select('id, cv_data')
        .not('cv_data', 'is', null);

      if (error) throw error;

      console.log(`Found ${submissions.length} submissions with CV data.`);
      let synced = 0;
      let errors = 0;

      // Process in chunks to avoid overwhelming the network/db
      const chunks = [];
      for (let i = 0; i < submissions.length; i += 5) {
        chunks.push(submissions.slice(i, i + 5));
      }

      for (const chunk of chunks) {
        const results = await Promise.all(chunk.map(async (sub) => {
          try {
            await this.syncCVToDocuments(sub.id, sub.cv_data);
            return true; // Success
          } catch (e) {
            console.error(`Failed to sync CV for submission ${sub.id}`, e);
            return false; // Error
          }
        }));

        results.forEach(success => {
          if (success) synced++;
          else errors++;
        });
      }

      return { total: submissions.length, synced, errors };
    } catch (error) {
      console.error('Error in batch CV sync:', error);
      throw error;
    }
  }

  /**
   * Get all submissions with optional filtering
   * @param {Object} filters - Optional filters (status, date range, etc.)
   * @returns {Array} - Array of submissions
   */
  static async getSubmissions(filters = {}) {
    try {
      let query = supabase
        .from('wizard_submissions')
        .select('*')
        .order('created_at', { ascending: false });

      // Apply filters if provided
      if (filters.status) {
        query = query.eq('status', filters.status);
      }

      if (filters.selectedPathway) {
        query = query.eq('selected_pathway', filters.selectedPathway);
      }

      if (filters.dateFrom) {
        query = query.gte('created_at', filters.dateFrom);
      }

      if (filters.dateTo) {
        query = query.lte('created_at', filters.dateTo);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching submissions:', error);
      throw error;
    }
  }

  /**
   * Update submission status
   * @param {string} submissionId - The submission ID
   * @param {string} status - New status
   * @param {string} adminNotes - Optional admin notes
   * @returns {Object} - Updated submission
   */
  static async updateSubmissionStatus(submissionId, status, adminNotes = null) {
    try {
      const updateData = { status };
      if (adminNotes) {
        updateData.admin_notes = adminNotes;
      }

      const { data, error } = await supabase
        .from('wizard_submissions')
        .update(updateData)
        .eq('submission_id', submissionId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating submission status:', error);
      throw error;
    }
  }

  /**
   * Upload CV file to Supabase Storage with proper RLS policies
   */
  static async uploadCV(file, userEmail) {
    try {
      // Create unique file name
      const fileExt = file.name.split('.').pop();
      const fileName = `${userEmail.replace(/[^a-zA-Z0-9]/g, '_')}_${Date.now()}.${fileExt}`;
      const filePath = `cvs/${fileName}`;

      // Upload file to Supabase Storage with public access
      const { data, error } = await supabase.storage
        .from('wizard-cvs')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type
        });

      if (error) {
        console.error('Supabase upload error:', error);
        throw error;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('wizard-cvs')
        .getPublicUrl(filePath);

      return {
        path: data.path,
        publicUrl: publicUrl,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      };
    } catch (error) {
      console.error('Error uploading CV:', error);
      throw error;
    }
  }

  /**
   * Delete CV file from Supabase Storage
   */
  static async deleteCV(filePath) {
    try {
      const { error } = await supabase.storage
        .from('wizard-cvs')
        .remove([filePath]);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting CV:', error);
      throw error;
    }
  }

  /**
   * Update CV analysis results
   */
  static async updateCVAnalysis(submissionId, cvData) {
    try {
      const { data, error } = await supabase
        .from('wizard_submissions')
        .update({
          cv_data: cvData,
          updated_at: new Date().toISOString()
        })
        .eq('id', submissionId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating CV analysis:', error);
      throw error;
    }
  }

  /**
   * Generate submission summary for admin review
   * @param {Object} submission - Submission data
   * @returns {Object} - Formatted summary
   */
  static generateSubmissionSummary(submission) {
    const summary = {
      basicInfo: {
        name: `${submission.first_name} ${submission.last_name}`,
        email: submission.email,
        phone: submission.phone,
        dateSubmitted: new Date(submission.created_at).toLocaleDateString()
      },
      selections: {
        country: submission.country_selection?.selectedCountry || 'Not specified',
        pathway: submission.selected_pathway || 'Not determined',
        package: submission.selected_package || 'Not selected'
      },
      contact: {
        preference: submission.contact_preference || 'email'
      }
    };

    // Add education summary
    if (submission.education_info?.highestEducation) {
      summary.education = `${submission.education_info.highestEducation} in ${submission.education_info.fieldOfStudy || 'Not specified'}`;
    }

    // Add experience summary
    if (submission.experience_info && Array.isArray(submission.experience_info) && submission.experience_info.length > 0) {
      const exp = submission.experience_info[0];
      summary.experience = `${exp.totalExperience || 'Not specified'} as ${exp.jobTitle || 'Not specified'}`;
    }

    // Add score summary
    if (submission.pathway_score?.bestMatch) {
      summary.score = {
        pathway: submission.pathway_score.bestMatch.pathway,
        score: submission.pathway_score.bestMatch.score,
        percentage: submission.pathway_score.bestMatch.result?.percentageScore || 0
      };
    }

    // Add CV summary
    if (submission.cv_data) {
      summary.cv = {
        uploaded: !!submission.cv_data.url,
        analyzed: !!submission.cv_data.aiAnalysis,
        fileName: submission.cv_data.fileName || 'Not specified'
      };
    }

    return summary;
  }
}

export default WizardSubmissionService;